/**
 * Created by piotr.pozniak@thebeaverhead.com on 24/10/2018.
 */

import React, { useContext, useEffect, useMemo } from "react";

import SidebarTab from "../../component/WidgetSettings/SidebarTab";
import MandatoryInformation from "./SettingsTabs/MandatoryInformation";
import SuccessAlert from "../../component/SuccessAlert";
import ApiError from "../../component/ApiError";
import CloneSettings from "./SettingsTabs/CloneSettings";
import LoadingIndicator from "../../../calendar/components/LoadingIndicator";
import FeaturedEventsFiltering from "./SettingsTabs/FeaturedEventsFiltering";
import { useCalendarStore } from "../../../../hooks/redux/calendar";
import { useFiltersStore } from "../../../../hooks/redux/filters";
import IntegrationContext from "../../../calendar/strategies/IntegrationContext";
import FormError from "../../component/FormError";
import FiltersSection from "./SettingsTabs/FiltersSection";
import classNames from "classnames";
import SEOSettings from "../../templates/WidgetPageTemplate/SettingsTabs/SEOSettings";

/**
 *
 * @param {object} props
 * @param {object} props.calendarModel - The calendar model object.
 * @param {object} props.errors - The errors object containing form errors.
 * @param {object} props.calendar - The calendar object for event filters.
 * @param {Array} props.integrations - The list of integrations available.
 * @param {boolean} props.disabled - Whether the form is disabled.
 * @param {function} props.onSubmit - Function to call on form submission.
 * @param {function} props.onUpdateField - Function to update a field in the form.
 * @param {function} props.onUpdateFilterSettings - Function to update filter settings.
 * @param {function} props.onUpdateWidgetSettings - Function to update widget settings.
 * @param {boolean} props.isNew - Whether the calendar is new.
 * @param {function} props.overwriteDesignProp - Function to overwrite design properties.
 * @param {function} props.overwriteFilterProp - Function to overwrite filter properties.
 * @param {object} props.user - The user object to determine the user's plan.
 * @param {boolean} props.hasSetupChanged - Whether the setup has changed.
 *
 * @returns {Element}
 * @constructor
 */
const SetupTab = (props) => {
  const { calendar } = useCalendarStore();
  const { integrations, hasSetupChanged } = props;

  const integrationStrategy = useContext(IntegrationContext);

  const integrationModel = useMemo(
    () =>
      calendar.model
        ? integrations.find((i) => i.uuid === calendar.model.integration.uuid)
        : {},
    [calendar.model.integration]
  );

  const { fetchIntegrationFilters, clearFilters } = useFiltersStore();

  useEffect(
    () => {
      fetchIntegrationFilters(
        calendar.model.integration.uuid,
        integrationStrategy.availableFilters,
        calendar.model.integration.type
      );

      return () => {
        clearFilters();
      };
    },
    [
      // TODO Confirm if this is the right way to do this. Does it need to re-run when integrationModel changes?
      /*integrationModel, integrationStrategy.availableFilters*/
    ]
  );

  const cloneSettings = (
    <CloneSettings
      overwriteDesignProp={props.overwriteDesignProp}
      overwriteFilterProp={props.overwriteFilterProp}
    />
  );

  const eventsFiltering =
    calendar.model && calendar.model.uuid ? (
      <FiltersSection
        user={props.user}
        calendarModel={props.calendarModel || {}}
        onUpdateField={props.onUpdateField}
        disabled={props.disabled}
        integrationModel={integrationModel}
        formErrors={props.errors}
        onUpdateFilterSettings={props.onUpdateFilterSettings}
      />
    ) : null;

  const featuredFiltering =
    calendar.model && calendar.model.uuid ? (
      <FeaturedEventsFiltering
        widgetModel={props.calendarModel || {}}
        onUpdateField={props.onUpdateField}
        onUpdateFilterSettings={props.onUpdateFilterSettings}
        disabled={props.disabled}
        formErrors={props.errors}
        onUpdateWidgetSettings={props.onUpdateWidgetSettings}
      />
    ) : null;

  const footer = !props.disabled ? (
    <div className="d-flex justify-content-end">
      {!calendar.updateSuccess && !calendar.updateError ? (
        <button
          disabled={props.disabled}
          type="submit"
          className="btn btn-primary hidden-xs"
          onClick={props.onSubmit}
        >
          Save
        </button>
      ) : null}
    </div>
  ) : (
    <LoadingIndicator />
  );

  return (
    <SidebarTab id="setup_tab" show={true} footer={footer}>
      {eventsFiltering}
      {featuredFiltering}
      <SEOSettings
        widgetModel={props.calendarModel || {}}
        disabled={props.disabled}
        onFieldChange={props.onUpdateWidgetSettings}
      />
      <MandatoryInformation
        calendarModel={props.calendarModel || {}}
        integrations={integrations}
        onUpdateField={props.onUpdateField}
        disabled={props.disabled}
        formErrors={props.errors}
      />
      {cloneSettings}
      {calendar.updateSuccess ? (
        <SuccessAlert className={"saved-work-warning-message"}>
          <i className={"material-icons"}>check_circle</i> Your changes have
          been saved.
        </SuccessAlert>
      ) : null}
      {Object.keys(props.errors).length ? (
        <FormError errors={Object.values(props.errors)} />
      ) : null}
      <ApiError
        className={"saving-work-error-message alert-danger"}
        error={calendar.updateError}
        defaultErrorMessage={"Could not save your settings. Please try again"}
      />
      {hasSetupChanged ? (
        <div
          className={classNames("alert alert-warning unsaved-work-warning", {
            "justify-content-end": !hasSetupChanged,
          })}
        >
          <div className="unsaved-work-warning-message">
            <i className={"material-icons"}>error</i>
            Save your changes to make them visible to others!
          </div>
        </div>
      ) : null}
    </SidebarTab>
  );
};

export default SetupTab;
